import { Lock } from "lucide-react";
import t from "@/lang/lang";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Address } from "@/services/backend/addresses/types/address";
import { cn } from "@/shared/lib/utils";

interface ReadOnlyAddressCardProps {
  address: Address;
  title?: string;
  description?: string;
}

/**
 * ReadOnlyAddressCard renders an address as plain text instead of a form.
 *
 * Use it whenever an address is maintained in another system and therefore
 * must not be edited in the GSM. Disabled input fields are not sufficient
 * here, since they read as "editing is currently unavailable" rather than
 * "this data is not maintained in this application".
 */
export function ReadOnlyAddressCard({
  address,
  title = t("Adresse"),
  description = undefined,
}: ReadOnlyAddressCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
        <CardAction>
          <Badge variant="secondary">
            <Lock />
            {t("Nur Lesen")}
          </Badge>
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <AddressValue label={t("Firma")} value={address.company} />
        <AddressValue label={t("Abteilung")} value={address.department} />
        <AddressValue label={t("Ansprechpartner")} value={address.contact} />
        <AddressValue label={t("Straße")} value={address.street} />
        <div className="flex space-x-4">
          <AddressValue
            className="max-w-xs"
            label={t("PLZ")}
            value={address.postalCode}
          />
          <AddressValue label={t("Stadt")} value={address.city} />
        </div>
        <AddressValue label={t("Land")} value={address.country} />
      </CardContent>
    </Card>
  );
}

function AddressValue({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  const isEmpty = value.trim() === "";

  return (
    <div className={cn("grid w-full items-center gap-1.5", className)}>
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className={cn("text-sm", isEmpty && "text-muted-foreground")}>
        {isEmpty ? "–" : value}
      </span>
    </div>
  );
}
